import type { ReactNode } from 'react';
import { renderChild } from '@/app_lib/htmlParser/parsers/parserOptions';
import type { Element, ParserProps } from '@/app_lib/htmlParser/types';
import { cn } from '@/app_lib/utils/utils';

export const OptimizedTableParser = ({ domNode }: ParserProps) => {
  const table = domNode.children.find((node) => (node as Element).name === 'table') as Element;
  const figcaption = domNode.children.find((node) => (node as Element).name === 'figcaption') as Element;

  // find header cells
  const thead = table?.children.find((node) => (node as Element).name === 'thead') as Element | undefined;
  const headerRow = thead?.children.find((node) => (node as Element).name === 'tr') as Element | undefined;
  const headerCells = headerRow?.children.filter((node) => {
    const element = node as Element;
    return element.name === 'th' || element.name === 'td';
  }) as Element[];

  // find body cells
  const tbody = table?.children.find((node) => (node as Element).name === 'tbody') as Element;
  const bodyRows = tbody.children.filter((node) => (node as Element).name === 'tr') as Element[];

  // construct new rows for mobile view
  const newRows: ReactNode[] = [];
  for (let i = 0; i < bodyRows.length; i++) {
    const rowLength = bodyRows[i].children.length;

    const hasLinkInLastCell = (bodyRows[i].children[rowLength - 1] as Element).children.some(
      (node) => (node as Element).name === 'a',
    );

    // header is always first and possibly last cell (if there's a link in the last cell)
    const firstCell = bodyRows[i].firstChild as Element;
    const hasFeatured = firstCell.children.some((node) =>
      (node as Element).attribs?.class?.includes('has-vivid-green-cyan-color'),
    );
    const firstRow = (
      <tr
        className={cn('header-row', hasFeatured && 'border-x-2 border-t-2 border-b-0! stroke-accent! bg-blue-50!')}
        key={`${i}${0}`}
      >
        <th>{renderChild(firstCell.children)}</th>
        {hasLinkInLastCell ? <th>{renderChild((bodyRows[i].lastChild as Element).children)}</th> : <th />}
      </tr>
    );
    newRows.push(firstRow);

    for (let j = 1; j < rowLength; j++) {
      if (j === rowLength - 1 && hasLinkInLastCell) {
        break;
      }

      // base case: map header cell to body cell
      const headerCell = headerCells?.[j];
      const bodyCell = bodyRows[i].children[j] as Element;
      const newRow = (
        // NOTE: this sucks. I hate this. But I don't have the time to refactor the desktop version and the css to make this cleaner
        <tr
          className={cn(
            hasFeatured &&
              // HACK: we don't really want to create new stroke classes as this is most likely a one-off
              'featured border-x-2 stroke-accent border-b-neutral-200 dark:border-b-neutral-700 bg-blue-50!',
            j === rowLength - 2 && hasLinkInLastCell && hasFeatured && 'border-b-2 stroke-accent!',
          )}
          key={`${i}${j}`}
        >
          {headerCell ? <td>{renderChild(headerCell.children)}</td> : <td />}
          <td>{renderChild(bodyCell.children)}</td>
        </tr>
      );
      newRows.push(newRow);
    }
  }

  return (
    <figure className='optimized-table-widget'>
      {table?.children && (
        <>
          <div className='overflow-x-auto max-md:hidden'>
            <table>{renderChild(table.children)}</table>
          </div>
          <div className='md:hidden'>
            {figcaption && <figcaption className='caption'>{renderChild(figcaption.children)}</figcaption>}
            <table>
              <tbody>{newRows}</tbody>
            </table>
          </div>
        </>
      )}
    </figure>
  );
};
